import { useEffect, useState } from "react";
import { CSSObject } from "@emotion/react";
import { IonSegment, IonSegmentButton } from "@ionic/react";
import { DateTime } from "luxon";
import { SchoolClosure, SchoolModel } from "model";
import { Colors, Icon, useFeatureFlags } from "swing-components";
import { translate } from "utils/string-table";
import { v4 as uuid } from "uuid";

import { Footer, ProfileCard } from "../ProfileCard";
import { tabSegmentStyles } from "../SchoolYears/SchoolYearsView";
import { ClosureField, ClosureFieldRow } from "./ClosureFieldRow";
import { ClosuresListItem } from "./ClosureListItem";
import { countDays, currentAndNextAcademicYear } from "./utils";

export type ClosureMap = Map<string, ClosureField>;

function createClosureMap(closures: SchoolClosure[]) {
  const sorted = closures?.sort((a, b) => (a.start < b.start ? -1 : 1));
  return new Map(sorted.map((c) => [c.id, c]));
}

type SchoolClosuresViewProps = {
  school: SchoolModel;
  onSubmit: (closures: SchoolClosure[]) => void;
};

export function SchoolClosuresView(props: SchoolClosuresViewProps) {
  const { school, onSubmit } = props;
  const { closures } = school;

  const [isEditing, setIsEditing] = useState(false);
  const [tabs, setTabs] = useState<"currentYear" | "nextYear">("currentYear");
  const { flags } = useFeatureFlags();
  const nsdv2 = Boolean(flags.nsdv2 ?? false);

  const [_closures, _setClosures] = useState<ClosureMap>(createClosureMap(closures));

  const closuresArray = [..._closures.entries()].map(([, obj]) => obj);
  const daysOff = countDays(closures);

  const headerString = translate("SCHOOL_CLOSURES_DAYS_HEADER");
  // Hide school days count if non-school days v2 is enabled
  const header = nsdv2 ? `${headerString}` : `${headerString} (${daysOff})`;

  useEffect(() => {
    _setClosures(createClosureMap(closures));
  }, [closures]);

  const years = currentAndNextAcademicYear(DateTime.now());

  const filterStart = years[tabs === "currentYear" ? 0 : 1].startDate;
  const filterEnd = years[tabs === "currentYear" ? 0 : 1].endDate.plus({ days: 1 });

  // remove any closure dates that have been "removed"
  const activeClosures = closuresArray.filter((closure) => !closure.isRemoved);
  const isSameData = JSON.stringify(closures) === JSON.stringify(activeClosures);
  const isFormFilledOut = closuresArray.every(({ start, end, reason }) => start && end && reason);

  const relevantClosures = [..._closures.entries()].filter(
    ([, closure]) =>
      // show the temporary new closure
      !closure.id ||
      (closure.start >= filterStart.toISODate() && closure.start <= filterEnd.toISODate()),
  );

  return (
    <ProfileCard header={header} isEditing={isEditing} onClick={() => setIsEditing(true)}>
      {translate("SCHOOL_CLOSURES_DAYS_DESCRIPTION")}
      {nsdv2 && (
        <IonSegment
          disabled={isEditing}
          value={tabs}
          css={{ width: "100%", display: "flex" }}
          onIonChange={(e) => setTabs(e.target.value as "currentYear" | "nextYear")}
        >
          <IonSegmentButton css={{ flex: "1 1 auto" }} value="currentYear">
            {years[0].startDate.year}-{years[0].endDate.year}
          </IonSegmentButton>
          <IonSegmentButton css={{ flex: "1 1 auto" }} value="nextYear">
            <div css={tabSegmentStyles}>
              {years[1].startDate.year}-{years[1].endDate.year}
            </div>
          </IonSegmentButton>
        </IonSegment>
      )}

      {isEditing ? (
        <div css={styles.listContainer}>
          <ul css={styles.list}>
            {relevantClosures.map(([key, closure]) => (
              <ClosureFieldRow
                id={key}
                key={key}
                closureField={closure}
                closures={closuresArray}
                school={school}
                setClosure={_setClosures}
                currentActiveTab={tabs}
              />
            ))}
          </ul>
          <div css={styles.buttonContainer}>
            <button
              css={styles.button}
              onClick={() => {
                _setClosures((prevState) => {
                  const newMap = new Map(prevState);
                  // FIXME: We should not create UUID on the FE since causes
                  // confusion when looking at the date structure to determine
                  // if this closure is synced with the API/DB.
                  const id = uuid();
                  // Creates a new temporary school closure field
                  // All values need to be truthy in order for the admin to submit a
                  // new closure
                  return newMap.set(id, {
                    id: undefined,
                    start: undefined,
                    end: undefined,
                    reason: undefined,
                    isRemoved: false,
                  } as unknown as SchoolClosure);
                });
              }}
            >
              <Icon name="Add" color={Colors.blue500} />
              {translate("SCHOOL_CLOSURES_ADD_DAYS_LABEL")}
            </button>
          </div>
          <Footer
            disabled={isSameData || !isFormFilledOut}
            onSubmit={() => {
              onSubmit(closuresArray);
              setIsEditing(false);
            }}
            onCancel={() => {
              _setClosures(createClosureMap(closures));
              setIsEditing(false);
            }}
          />
        </div>
      ) : (
        <ul css={styles.list}>
          {relevantClosures?.length > 0 ? (
            relevantClosures.map(([id, closure]) => <ClosuresListItem key={id} closure={closure} />)
          ) : (
            <div>{translate("SCHOOL_CLOSURES_EMPTY_LABEL")}</div>
          )}
        </ul>
      )}
    </ProfileCard>
  );
}

const styles: Record<string, CSSObject> = {
  buttonContainer: {
    display: "flex",
    placeContent: "center",
  },
  button: {
    display: "flex",
    padding: 16,
    fontSize: 16,
    color: Colors.blue500,
    fontWeight: 500,
    background: "transparent",
    lineHeight: 1.5,
  },
  listContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  list: {
    padding: 0,
    listStyle: "none",
  },
};
